"use client"

import * as React from "react"

import { Button } from "@/components/ui/button"
import { WheelAmbient } from "@/components/wheel/ambient"
import { PrizeWheel } from "@/components/wheel/prize-wheel"
import { wheelAudio } from "@/components/wheel/wheel-audio"
import { PrizeReveal } from "@/components/wheel/reveal"
import { DEFAULT_VFX, type SpinPhase, type WheelVfx } from "@/components/wheel/types"
import { grantPrize } from "@/lib/wheel/grant"
import { PRIZES, drawForced, drawPrize, type WheelPrize } from "@/lib/wheel/prizes"
import { TOP_PRIZES, selectStartFeed, useDropFeed } from "@/lib/wheel/drop-feed"
import {
  selectAddSpin,
  selectNextFreeAt,
  selectRecordSpin,
  selectSpinHistory,
  selectSpinsLeft,
  selectTickSpins,
  selectUseSpin,
  useSpinsStore,
} from "@/lib/wheel/spins-store"
import { cn } from "@/lib/utils"

/* The floor. One wheel under a lamp, one button, the rail beside it. The
   draw is decided before the wheel moves (drawPrize), the wheel only lands
   on it; the reveal then grants it into the real stores.

   Two homes: the /wheel page (with the rail) and the takeover that the
   Monthly Run opens over the creator shell (without it). */

function hms(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  if (total < 60) return `${total}s`
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":")
}

/* The field recipe (creator-copy/field-card) at page scale: dark ground, the
   lamp from top centre behind the wheel, halftone fading down, bloom from
   the bottom left, the mark sunk bottom right. */
/** The floor's ground. Solid on /wheel; over another screen it is only the
    lamp and the bloom, so the screen shows through the tint. */
function Ground({ solid }: { solid: boolean }) {
  return (
    <>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: solid
            ? "radial-gradient(60% 45% at 50% 0%, hsl(var(--primary) / 0.28) 0%, transparent 70%), linear-gradient(180deg, hsl(0 0% 3%) 0%, hsl(0 0% 6%) 55%, hsl(0 0% 9%) 100%)"
            : "radial-gradient(60% 45% at 50% 0%, hsl(var(--primary) / 0.22) 0%, transparent 70%)",
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1.4px)",
          backgroundSize: "7px 7px",
          opacity: 0.1,
          mixBlendMode: "overlay",
          maskImage: "linear-gradient(to bottom, black 0%, transparent 70%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 70%)",
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(90% 70% at 4% 100%, hsl(var(--primary) / 0.14) 0%, transparent 55%)",
        }}
      />
    </>
  )
}


/** Between the press and the wheel moving: the vignette closes in first. */
const SPIN_HOLD_MS = 1100
const VFX_KEY = "wheel-vfx-v2"
const VFX_LABEL: Record<keyof WheelVfx, string> = {
  rays: "Light rays",
  edge: "Breathing edge",
  flares: "Star flares",
  sparks: "Sparks with tails",
}

function readVfx(): WheelVfx {
  try {
    const raw = window.localStorage.getItem(VFX_KEY)
    if (raw) return { ...DEFAULT_VFX, ...(JSON.parse(raw) as Partial<WheelVfx>) }
  } catch {}
  return DEFAULT_VFX
}

interface QaPanelProps {
  vfx: WheelVfx
  onVfx: (next: WheelVfx) => void
  forced: string
  onForced: (id: string) => void
  onRefill: () => void
  onBigDrop: () => void
  onReset: () => void
}

/* Prototype QA only. Never shown without ?qa=1. */
function QaPanel({ vfx, onVfx, forced, onForced, onRefill, onBigDrop, onReset }: QaPanelProps) {
  return (
    <div className="fixed bottom-4 left-4 z-[320] flex w-[220px] flex-col gap-2 rounded-xl bg-grey-950 p-3 font-open-sauce text-[12px] text-white ring-1 ring-inset ring-white-100">
      <span className="font-semibold text-grey-400">QA</span>
      <div className="flex flex-col gap-1">
        {(Object.keys(VFX_LABEL) as (keyof WheelVfx)[]).map((key) => (
          <label key={key} className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={vfx[key]}
              onChange={(e) => onVfx({ ...vfx, [key]: e.target.checked })}
              className="size-3.5 accent-[hsl(var(--primary))]"
            />
            <span>{VFX_LABEL[key]}</span>
          </label>
        ))}
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-grey-400">Next spin lands on</span>
        <select
          value={forced}
          onChange={(e) => onForced(e.target.value)}
          className="h-8 rounded-md bg-grey-900 px-2 text-white ring-1 ring-inset ring-white-100"
        >
          <option value="">Random (weighted)</option>
          {PRIZES.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <Button type="button" variant="outline" size="sm" onClick={onRefill} className="h-8 rounded-md">
        +5 spins
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={onBigDrop} className="h-8 rounded-md">
        Big drop on the floor
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onReset} className="h-8 rounded-md text-grey-400">
        Reset spins
      </Button>
    </div>
  )
}

export interface WheelFloorProps {
  /** Draw the Recent drops rail beside the wheel. */
  rail?: boolean
  /** Paint a solid ground. Off when the floor sits over another screen. */
  solid?: boolean
}

export function WheelFloor({ rail = true, solid = true }: WheelFloorProps) {
  const spinsLeft = useSpinsStore(selectSpinsLeft)
  const nextFreeAt = useSpinsStore(selectNextFreeAt)
  const history = useSpinsStore(selectSpinHistory)
  const spendSpin = useSpinsStore(selectUseSpin)
  const addSpin = useSpinsStore(selectAddSpin)
  const record = useSpinsStore(selectRecordSpin)
  const tick = useSpinsStore(selectTickSpins)

  const [phase, setPhase] = React.useState<SpinPhase>("idle")
  const [prize, setPrize] = React.useState<WheelPrize | null>(null)
  const [targetIndex, setTargetIndex] = React.useState<number | null>(null)
  const [spinKey, setSpinKey] = React.useState(0)
  // Sound is always on; there is no mute on the floor.
  const sound = true
  const [now, setNow] = React.useState(() => Date.now())

  // One clock for the countdown and the store's free-spin refill.
  // Prototype refill: /wheel?spins=5 tops the counter up so the floor can be
  // walked more than once a day while there is no backend issuing spins.
  // QA panel behind /wheel?qa=1: force the next prize, refill, fire a big
  // drop on the floor, reset the spin wallet.
  const spinStartRef = React.useRef(0)
  /** The wheel's box, so the vignette's clear pool sits on it whatever the
      screen: phones stack the rail under it, laptops cap it by height. */
  const wheelBoxRef = React.useRef<HTMLDivElement>(null)
  const [pool, setPool] = React.useState({ x: "50%", y: "50%", r: "320px" })
  const measurePool = React.useCallback(() => {
    const box = wheelBoxRef.current?.getBoundingClientRect()
    if (!box) return
    setPool({ x: `${box.left + box.width / 2}px`, y: `${box.top + box.height / 2}px`, r: `${box.width / 2}px` })
  }, [])
  /** True once a spin has run, so the release animation has something to release. */
  const [dimmed, setDimmed] = React.useState(false)
  React.useEffect(() => {
    if (phase === "spinning") setDimmed(true)
  }, [phase])
  React.useEffect(() => () => window.clearTimeout(spinStartRef.current), [])
  const [qa, setQa] = React.useState(false)
  const [forced, setForced] = React.useState<string>("")
  const [vfx, setVfx] = React.useState<WheelVfx>(DEFAULT_VFX)
  React.useEffect(() => setVfx(readVfx()), [])
  const updateVfx = (next: WheelVfx) => {
    setVfx(next)
    try {
      window.localStorage.setItem(VFX_KEY, JSON.stringify(next))
    } catch {}
  }
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const n = Number(params.get("spins"))
      if (n > 0) addSpin(n)
      setQa(params.get("qa") === "1")
    } catch {}
  }, [addSpin])

  React.useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now())
      tick()
    }, 1000)
    return () => window.clearInterval(id)
  }, [tick])

  // settled -> revealed two seconds later. The win sound fires at the stop,
  // so the wheel, the lamps and the sound have their moment before the card
  // takes over.
  React.useEffect(() => {
    if (phase !== "settled") return
    if (sound && prize) wheelAudio.win(TOP_PRIZES.has(prize.id))
    const id = window.setTimeout(() => setPhase("revealed"), 2000)
    return () => window.clearTimeout(id)
  }, [phase, prize, sound])

  const spin = () => {
    if (phase !== "idle" || spinsLeft <= 0) return
    if (!spendSpin()) return
    // Audio needs a user gesture to start; this is the one. The whoosh goes
    // with the room going dark, before the wheel moves.
    wheelAudio.unlock()
    if (sound) wheelAudio.start()
    const draw = forced ? drawForced(forced) : drawPrize()
    setPrize(draw.prize)
    setTargetIndex(draw.segmentIndex)
    measurePool()
    setPhase("spinning")
    // The room starts going dark first; the wheel takes torque a beat later.
    window.clearTimeout(spinStartRef.current)
    spinStartRef.current = window.setTimeout(() => setSpinKey((k) => k + 1), SPIN_HOLD_MS)
  }

  const onSettled = React.useCallback(() => setPhase("settled"), [])

  const onClaim = () => {
    if (prize) {
      // The realest thing: the prize goes into the stores the slip and the
      // header credit already read from.
      grantPrize(prize)
      record(prize.id)
    }
    // A spin back is not a win to post: take it and get out of the way, so
    // the player is on the wheel again instead of looking at a share card.
    if (prize?.kind === "respin") {
      setPhase("idle")
      setPrize(null)
      setTargetIndex(null)
      return
    }
    setPhase("claimed")
  }

  const onDismiss = () => {
    setPhase("idle")
    setPrize(null)
    setTargetIndex(null)
  }

  const startFeed = useDropFeed(selectStartFeed)
  React.useEffect(() => startFeed(), [startFeed])
  // Samples decoded before the first press, so the first spin has them.
  React.useEffect(() => wheelAudio.preload(), [])

  // The reveal dialog portals to body; the page cover sits at z-300, so the
  // dialog is lifted above it while this page is on (see globals.css /wheel).
  React.useEffect(() => {
    document.body.classList.add("wheel-floor")
    return () => document.body.classList.remove("wheel-floor")
  }, [])

  const busy = phase !== "idle"
  const out = spinsLeft <= 0
  const hasWin = history.length > 0
  const label = busy ? "Spinning" : out ? `Next spin in ${hms(nextFreeAt - now)}` : "Spin"

  return (
    <main
      className={cn(
        "relative isolate flex flex-col justify-center overflow-x-hidden px-4 py-6",
        // Under the /wheel bar, or the full screen when floating over another one.
        solid ? "min-h-[calc(100vh-3rem)]" : "min-h-screen"
      )}
    >
      <Ground solid={solid} />
      {/* The room goes dark while the wheel runs: a vignette closes in from
          the edges over the spin and lets go once the wheel has stopped, so
          the eye is on the wheel and nothing else. */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none fixed inset-0 z-[305] opacity-0 motion-reduce:hidden",
          // In: most of the way down in the first third, then still deepening
          // until the wheel dies. Out: a slow release once it has stopped.
          phase === "spinning" ? "wheel-dim-in" : dimmed ? "wheel-dim-out" : ""
        )}
        style={{
          // The clear pool is the wheel's own radius plus a margin, in px, so
          // the wheel never darkens whatever the screen shape. The wheel sits
          // on the centre line of the area under the 3rem bar.
          ["--wheel-r" as string]: pool.r,
          background: `radial-gradient(circle at ${pool.x} ${pool.y}, transparent 0, transparent calc(var(--wheel-r) + 24px), hsl(var(--black-0) / 0.7) calc(var(--wheel-r) + 200px), hsl(var(--black-0) / 0.97) calc(var(--wheel-r) + 520px), hsl(var(--black-0) / 1) 100%)`,
        }}
      />
      {/* Three columns, the outer two the same width, so the wheel sits on the
          centre line of the screen with the rail off to the right. */}
      <div
        className={cn(
          "relative mx-auto grid w-full max-w-[1320px] grid-cols-1 items-center gap-8",
          rail && "lg:grid-cols-[260px_minmax(0,1fr)_260px] lg:gap-6 xl:grid-cols-[340px_minmax(0,1fr)_340px] xl:gap-10"
        )}
      >
        {rail && <div aria-hidden className="hidden lg:block" />}
        <div className="flex flex-col items-center">
          <div ref={wheelBoxRef} className="relative w-full max-w-[max(300px,min(640px,calc(100vh-250px)))]">
            <PrizeWheel
              vfx={vfx}
              targetIndex={targetIndex}
              spinKey={spinKey}
              onSettled={onSettled}
              sound={sound}
              className="aspect-square w-full"
            />
          </div>

          <p className="mt-6 font-open-sauce text-sm font-semibold text-white-600">
            <span className="font-black text-white">{spinsLeft}</span> {spinsLeft === 1 ? "spin" : "spins"}
          </p>
          <Button
            type="button"
            onClick={spin}
            disabled={busy || out}
            className={cn("mt-2 h-14 w-full max-w-[320px] rounded-full text-base", out && "tabular-nums")}
          >
            {label}
          </Button>
          {out && hasWin && !busy && (
            <p className="mt-2 font-open-sauce text-sm text-grey-400">Share a win to earn a spin</p>
          )}

          <div className="w-full max-w-[640px]">
            <PrizeReveal
              prize={prize}
              phase={phase}
              onClaim={onClaim}
              onShared={() => addSpin(1)}
              onDismiss={onDismiss}
            />
          </div>

        </div>

        {rail && <WheelAmbient history={history} />}
      </div>
      {qa && (
        <QaPanel
          vfx={vfx}
          onVfx={updateVfx}
          forced={forced}
          onForced={setForced}
          onRefill={() => addSpin(5)}
          onBigDrop={() => {
            const ids = [...TOP_PRIZES]
            useDropFeed.getState().push({
              id: `qa-${Date.now()}`,
              wallet: "0x7a1c…f0e9",
              prizeId: ids[Math.floor(Math.random() * ids.length)],
              at: Date.now(),
            })
          }}
          onReset={() => {
            try {
              window.localStorage.removeItem("wheel-spins-v2")
            } catch {}
            window.location.reload()
          }}
        />
      )}
    </main>
  )
}
